<template>
    <div>
        <h2 class="text-2.5xl font-bold">
            {{ question.metadata.title }}
        </h2>
        <JsonSchemaCustomForm
            v-if="controller"
            v-slot="{ fields }"
            v-model="controller.value"
            :controller="controller"
        >
            <JsonSchemaCustomFormElement
                v-for="(field, key) in fields"
                :key="key"
                :field="field"
                :custom-component-input="getCustomElement"
                @input="onChange"
            />
        </JsonSchemaCustomForm>
    </div>
</template>

<script lang="ts">
    import type { Component as VueComponentType } from 'vue';

    import cloneDeep from 'lodash/cloneDeep';
    import { Component } from 'vue-property-decorator';

    import {
        JsonSchemaCustomForm,
        JsonSchemaCustomFormElement,
    } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import JsonForm, { JsonFormType } from '@evidentid/json-schema/interfaces/JsonForm';
    import { isObject } from '@evidentid/json-schema/schemaChecks';

    import JsonSchemaArrayForm from '@/components/forms/JsonSchemaForm/JsonSchemaArrayForm.vue';
    import JsonSchemaBooleanForm from '@/components/forms/JsonSchemaForm/JsonSchemaBooleanForm.vue';
    import JsonSchemaConstForm from '@/components/forms/JsonSchemaForm/JsonSchemaConstForm.vue';
    import JsonSchemaEnumForm from '@/components/forms/JsonSchemaForm/JsonSchemaEnumForm.vue';
    import JsonSchemaNumberForm from '@/components/forms/JsonSchemaForm/JsonSchemaNumberForm.vue';
    import JsonSchemaObjectForm from '@/components/forms/JsonSchemaForm/JsonSchemaObjectForm.vue';
    import JsonSchemaStringForm from '@/components/forms/JsonSchemaForm/JsonSchemaStringForm.vue';
    import LicenseIdInputForm from '@/components/forms/JsonSchemaForm/LicenseIdInputForm.vue';
    import LicenseStateObjectForm from '@/components/forms/JsonSchemaForm/LicenseStateObjectForm.vue';
    import FieldComponent from '@/fields/FieldComponent';
    import SmartAttributeField from '@/fields/SmartAttributeField';
    import { licenseStateIdsWithCustomValidation } from '@/utils/licenseValidation/constants';

    // PRODUCT-26110 Phase 2B2 Task 5 batch 4: see JsonSchemaFormElement.vue's identical
    // note -- `Vue.Component`'s ambient alias collides with a stray vue@2.7.16 UMD
    // global program-wide (batch 1's established fix). Imported as `VueComponentType`
    // to avoid colliding with the `Component` decorator import above.
    const elementByType: Record<JsonFormType, VueComponentType> = {
        string: JsonSchemaStringForm,
        number: JsonSchemaNumberForm,
        boolean: JsonSchemaBooleanForm,
        const: JsonSchemaConstForm,
        enum: JsonSchemaEnumForm,
        object: JsonSchemaObjectForm,
        array: JsonSchemaArrayForm,
    };

    @Component({
        emits: [ 'change' ],
        components: { JsonSchemaCustomForm, JsonSchemaCustomFormElement },
    })
    export default class SmartAttribute extends FieldComponent<any> {
        // A getter, not a field: a field initializer runs while the component is being
        // constructed, before vue-facing-decorator has wired up the props, so `field` is
        // still undefined there. Reading it at render time also means the controller
        // follows a `field` that changes rather than being fixed at construction.
        private get controller() {
            return (this.field as SmartAttributeField).smartAttributeFormController;
        }
        private getCustomElement(form: JsonForm): VueComponentType {
            if (this.isLicenseStateForm(form)) {
                return LicenseStateObjectForm;
            }
            if (this.isCustomValidatedLicenseIdInput(form)) {
                return LicenseIdInputForm;
            }
            return elementByType[form.type];
        }

        private isLicenseStateForm(form: JsonForm): boolean {
            return Boolean(
                isObject(form.schema) &&
                    form.schema.properties.licenseState &&
                    this.isLicenseState(),
            );
        }

        private isCustomValidatedLicenseIdInput(form: JsonForm): boolean {
            return Boolean(
                form.schema.$id === '#licenseString' && this.isLicenseState(),
            );
        }

        private isLicenseState(): boolean {
            return licenseStateIdsWithCustomValidation
                .some((item: string) => this.controller?.form.schema.$id?.includes(item));
        }

        private onChange() {
            this.$emit('change', cloneDeep(this.controller?.value || null));
        }
    }
</script>
